<script setup lang="ts">
  import { computed } from 'vue';

  import type { Task, TaskRequirement, TaskRequirementOption } from '@stuut/api/models';
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/components/collapsible';

  import {
    FileDropzoneContent,
    FileDropzoneOverlay,
    FileDropzoneProvider,
  } from '@/components/file-upload-dropzone';

  import TaskRequirementRow from './TaskRequirementRow.vue';

  const props = defineProps<{
    task: Task;
    requirements: TaskRequirement[];
    partnerId?: string;
  }>();

  const emit = defineEmits<{
    (e: 'selectOption', requirement: TaskRequirement, option: TaskRequirementOption): void;
    (e: 'approve', requirement: TaskRequirement): void;
    (e: 'reject', requirement: TaskRequirement): void;
    (e: 'dismiss', requirement: TaskRequirement): void;
    (e: 'resolve', requirement: TaskRequirement, value: string): void;
  }>();

  const isDroppable = (req: TaskRequirement) => req.input_type === 'file';

  const fileTypes = (req: TaskRequirement) => {
    const accepted = req.acceptedFileTypes;
    return accepted.length ? accepted : undefined;
  };

  const openRequirements = computed(() =>
    props.requirements.filter((req) => req.isOpen || req.isAnswered || req.isSkipped),
  );

  const resolvedRequirements = computed((): TaskRequirement[] =>
    props.requirements.filter((req) => !req.isOpen && !req.isAnswered && !req.isSkipped),
  );
</script>

<template>
  <div v-if="requirements.length" class="space-y-4">
    <Collapsible v-if="resolvedRequirements.length" v-slot="{ open }">
      <CollapsibleTrigger
        class="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      >
        <div class="w-4 flex items-center justify-center">
          <i
            class="fas text-2xs transition-transform"
            :class="open ? 'fa-chevron-down' : 'fa-chevron-right'"
          />
        </div>
        <span>Completed ({{ resolvedRequirements.length }})</span>
      </CollapsibleTrigger>

      <CollapsibleContent class="space-y-4 pt-3">
        <template v-for="req in resolvedRequirements" :key="req.id">
          <FileDropzoneProvider
            v-if="isDroppable(req)"
            :disabled="!task?.isOpen"
            :file-types="fileTypes(req)"
          >
            <FileDropzoneContent class="relative">
              <TaskRequirementRow
                :requirement="req"
                :partner-id="partnerId"
                :task="task"
                @approve="emit('approve', req)"
                @reject="emit('reject', req)"
                @dismiss="emit('dismiss', req)"
                @resolve="(val) => emit('resolve', req, val)"
                @select-option="(opt) => emit('selectOption', req, opt)"
              />

              <FileDropzoneOverlay size="sm" />
            </FileDropzoneContent>
          </FileDropzoneProvider>

          <TaskRequirementRow
            v-else
            :requirement="req"
            :partner-id="partnerId"
            :task="task"
            @approve="emit('approve', req)"
            @reject="emit('reject', req)"
            @dismiss="emit('dismiss', req)"
            @resolve="(val) => emit('resolve', req, val)"
            @select-option="(opt) => emit('selectOption', req, opt)"
          />
        </template>
      </CollapsibleContent>
    </Collapsible>

    <template v-for="req in openRequirements" :key="req.id">
      <FileDropzoneProvider
        v-if="isDroppable(req)"
        :disabled="!task?.isOpen"
        :file-types="fileTypes(req)"
      >
        <FileDropzoneContent class="relative">
          <TaskRequirementRow
            :requirement="req"
            :partner-id="partnerId"
            :task="task"
            @approve="emit('approve', req)"
            @reject="emit('reject', req)"
            @dismiss="emit('dismiss', req)"
            @resolve="(val) => emit('resolve', req, val)"
            @select-option="(opt) => emit('selectOption', req, opt)"
          />

          <FileDropzoneOverlay size="sm" />
        </FileDropzoneContent>
      </FileDropzoneProvider>

      <TaskRequirementRow
        v-else
        :requirement="req"
        :partner-id="partnerId"
        :task="task"
        @approve="emit('approve', req)"
        @reject="emit('reject', req)"
        @dismiss="emit('dismiss', req)"
        @resolve="(val) => emit('resolve', req, val)"
        @select-option="(opt) => emit('selectOption', req, opt)"
      />
    </template>
  </div>
</template>
